import {
  ComponentPropsWithoutRef,
  forwardRef,
  useContext,
  MouseEvent,
} from "react";
import { Icon } from "../Icon";
import { Flex, FlexProps } from "../Flex";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import { DataTrackingId } from "../../types";
import { useListbox } from "./internal/ListboxContext";
import { useTrackingId } from "../../hooks";
import { ListboxOptionGroupContext } from "./internal/ListboxOptionGroupContext";

import cx from "classnames";
import styles from "./Listbox.module.scss";

type ItemType = {
  label: string;
  [key: string]: unknown;
};

/**
 * Props for the ListboxOption component
 * @extends ComponentPropsWithoutRef<"li">
 */
export type ListboxOptionProps = ComponentPropsWithoutRef<"li"> & {
  /** The value of the option */
  value?: string;
  /** Whether the option is disabled */
  disabled?: boolean;
  /** Flex alignment for items */
  alignItems?: FlexProps["alignItems"];
  /** Flex justification for items */
  justifyItems?: FlexProps["justifyItems"];
  /** Flex alignment for content */
  alignContent?: FlexProps["alignContent"];
  /** Flex justification for content */
  justifyContent?: FlexProps["justifyContent"];
  /** Flex place-items property */
  placeItems?: FlexProps["placeItems"];
  /** Flex place-content property */
  placeContent?: FlexProps["placeContent"];
} & (
    | {
        /** The item object when using items prop */
        item: ItemType;
        /** The display label for the option (optional if item is provided) */
        label?: string;
      }
    | {
        /** The display label for the option */
        label: string;
        /** No item object when using label directly */
        item?: never;
      }
  ) &
  DataTrackingId;

/**
 * ListboxOption component for individual selectable items within a listbox.
 *
 * Features:
 * - Displays selectable options with proper ARIA attributes
 * - Supports disabled state for non-selectable options
 * - Keyboard navigation support
 * - Accessible with screen reader support
 *
 * @example
 * <ListboxOption value="option1" label="Option 1" />
 */
export const ListboxOption = forwardRef<HTMLLIElement, ListboxOptionProps>(
  (props, ref) => {
    const {
      className,
      children,
      disabled: disabledProp,
      onClick,
      label: labelProp,
      item,
      value,
      alignItems,
      justifyItems,
      alignContent,
      justifyContent,
      placeItems,
      placeContent,
      ...rest
    } = props;

    const {
      setCurrentFocus,
      currentFocus,
      selected,
      setSelected,
      selectionMode,
      onSelectionChange,
      controlled,
    } = useListbox();

    const label = labelProp || item?.label || "";

    const { disabled: groupDisabled } = useContext(ListboxOptionGroupContext);
    const disabled =
      groupDisabled || disabledProp || (item?.disabled as boolean) || false;

    const isSelected = () => {
      if (selectionMode === "single") {
        if (item) {
          return selected?.label === label;
        }
        return selected === label;
      }
      if (selectionMode === "multiple") {
        const selectedItems = Array.isArray(selected)
          ? selected
          : selected
            ? [selected]
            : [];
        if (item) {
          return selectedItems.some(
            (selectedItem) => selectedItem.label === label,
          );
        }
        return selectedItems.includes(label);
      }
      return false;
    };

    const onClickHandler = (e: MouseEvent<HTMLLIElement>) => {
      if (disabled) return;

      onClick?.(e);
      setCurrentFocus?.(label);

      if (selectionMode === "single") {
        if (
          (!item && selected === label) ||
          (item && selected?.label === label)
        ) {
          if (!controlled) {
            setSelected?.(undefined);
          }
          onSelectionChange?.(undefined);
          return;
        }

        if (item) {
          if (!controlled) {
            setSelected?.(item);
          }
          onSelectionChange?.(item);
          return;
        }

        if (!item) {
          if (!controlled) {
            setSelected?.(label);
          }
          onSelectionChange?.(label);
          return;
        }
      }

      if (selectionMode === "multiple") {
        const selectedItems = Array.isArray(selected)
          ? selected
          : selected
            ? [selected]
            : [];
        if (
          item &&
          selectedItems.some((selectedItem) => selectedItem.label === label)
        ) {
          const selectedItemsWithItemRemoved = selectedItems.filter(
            (selectedItem) => selectedItem.label !== label,
          );
          setSelected?.(selectedItemsWithItemRemoved);
          onSelectionChange?.(selectedItemsWithItemRemoved);
          return;
        }

        if (!item && selectedItems.includes(label)) {
          const selectedItemsWithItemRemoved = selectedItems.filter(
            (selectedItem) => selectedItem !== label,
          );
          setSelected?.(selectedItemsWithItemRemoved);
          onSelectionChange?.(selectedItemsWithItemRemoved);
          return;
        }

        if (item) {
          const selectedItemsWithItemAdded = [...selectedItems, item];
          setSelected?.(selectedItemsWithItemAdded);
          onSelectionChange?.(selectedItemsWithItemAdded);
          return;
        }

        if (!item) {
          const selectedItemsWithItemAdded = [...selectedItems, label];
          setSelected?.(selectedItemsWithItemAdded);
          onSelectionChange?.(selectedItemsWithItemAdded);
          return;
        }
      }
    };

    const data = {
      value: props.value,
      label: "item" in props ? props.item?.label : props.label,
    };

    const trackingId = useTrackingId({
      name: "ListboxOption",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const ListboxOptionClassNames = cx(styles["option"], className, {});

    return (
      <li // eslint-disable-line jsx-a11y/click-events-have-key-events
        data-tracking-id={trackingId}
        className={ListboxOptionClassNames}
        data-anv="listbox-option"
        role="option"
        aria-disabled={disabled}
        data-focused={currentFocus === label}
        aria-selected={isSelected()}
        id={label}
        onClick={onClickHandler}
        ref={ref}
        value={value ?? label}
        {...rest}
      >
        <Flex
          className={styles["children"]}
          grow={1}
          shrink={1}
          alignItems={alignItems}
          justifyItems={justifyItems}
          alignContent={alignContent}
          justifyContent={justifyContent}
          placeItems={placeItems}
          placeContent={placeContent}
        >
          {children}
        </Flex>
        <Icon
          aria-hidden
          svg={Check}
          inherit
          className={styles["Icon"]}
          style={{ opacity: isSelected() ? 1 : 0 }}
        />
      </li>
    );
  },
);

ListboxOption.displayName = "ListboxOption";
